/**
 * CartaWarisTab Component
 * Visualizes the selected heirs (waris) as a family/inheritance tree chart.
 *
 * Supports two-way editing: +/- controls on each node update the shared heirs
 * state via onHeirChange, keeping Senarai Waris and Carta Waris in sync.
 *
 * Reference: FaraidInfo.md Section 3 (Waris-Waris)
 */

import { memo, useCallback, useMemo } from 'react';
import { Card } from '@components/common/Card';
import { Button } from '@components/common/Button';
import type { DeceasedInfo, HeirInput, HeirType } from '../../types';
import { HEIR_DEFINITIONS } from '../../types';
import styles from './CartaWarisTab.module.css';

// ============================================================================
// Props
// ============================================================================

interface CartaWarisTabProps {
  deceased: DeceasedInfo;
  heirs: Record<HeirType, HeirInput>;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

// ============================================================================
// SVG person icons -- two shapes: male (square shoulders) and female (curved)
// Inline SVG kept minimal; no external file dependency.
// ============================================================================

const MalePersonIcon = ({ size = 32 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Head */}
    <circle cx="16" cy="10" r="6" fill="currentColor" />
    {/* Body — broad shoulders, straight sides */}
    <path
      d="M6 28 C6 20 10 17 16 17 C22 17 26 20 26 28 Z"
      fill="currentColor"
    />
  </svg>
);

const FemalePersonIcon = ({ size = 32 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Head with hijab outline — rounded top, wider base */}
    <circle cx="16" cy="10" r="6" fill="currentColor" />
    {/* Hijab frame */}
    <path
      d="M8 13 Q8 6 16 6 Q24 6 24 13 L24 16 Q22 14 16 14 Q10 14 8 16 Z"
      fill="currentColor"
      opacity="0.6"
    />
    {/* Body — slightly tapered, feminine silhouette */}
    <path
      d="M8 28 C8 21 11 18 16 18 C21 18 24 21 24 28 Z"
      fill="currentColor"
    />
  </svg>
);

// ============================================================================
// Heirs that remain editable even when blocked (mirrors SenaraiWarisTab)
// ============================================================================

const ALWAYS_EDITABLE_HEIRS: ReadonlySet<HeirType> = new Set<HeirType>([
  'bapa',
  'ibu',
  'datuk',
  'nenek_sebelah_bapa',
  'nenek_sebelah_ibu',
]);

// ============================================================================
// HeirNode -- individual heir card with +/- edit controls
//
// Receives a single pre-resolved HeirInput (Option B from code review) so
// React.memo can do a shallow reference comparison without a custom comparator.
// Blocking and gender validity are computed by each caller section and passed
// as a disabled flag, keeping HeirNode purely presentational re: validation.
// ============================================================================

interface HeirNodeProps {
  heirType: HeirType;
  input: HeirInput;
  /** Show even when count is 0 (greyed out placeholder for sibling group) */
  showWhenAbsent?: boolean;
  /** Whether this node's controls are disabled (gender invalid or heir blocked) */
  disabled?: boolean;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

const HeirNode = memo(({
  heirType,
  input,
  showWhenAbsent = false,
  disabled = false,
  onHeirChange,
}: HeirNodeProps) => {
  const definition = HEIR_DEFINITIONS[heirType];
  const count = input.count;
  const isActive = count > 0;

  if (!isActive && !showWhenAbsent) return null;

  const { labelMs, gender, maxCount } = definition;

  const handleDecrement = useCallback(() => {
    const next = Math.max(0, count - 1);
    onHeirChange(heirType, { count: next, isPresent: next > 0 });
  }, [heirType, count, onHeirChange]);

  const handleIncrement = useCallback(() => {
    const next = Math.min(maxCount, count + 1);
    onHeirChange(heirType, { count: next, isPresent: next > 0 });
  }, [heirType, count, maxCount, onHeirChange]);

  const isMale = gender === 'lelaki';

  return (
    <div
      className={[
        styles.heirNode,
        isActive ? styles.nodeActive : styles.nodeInactive,
        isMale ? styles.nodeMale : styles.nodeFemale,
      ].join(' ')}
    >
      {/* Avatar circle with person silhouette */}
      <div className={styles.nodeAvatar}>
        {isMale ? <MalePersonIcon size={28} /> : <FemalePersonIcon size={28} />}
      </div>

      {/* Label */}
      <div className={styles.nodeLabel}>{labelMs}</div>

      {/* Count control row */}
      <div className={styles.nodeControls}>
        <Button
          variant="primary"
          size="sm"
          className={styles.counterButton}
          onClick={handleDecrement}
          disabled={disabled || count <= 0}
          aria-label={`Kurangkan ${labelMs}`}
        >
          -
        </Button>
        <span className={[styles.countBadge, isActive ? styles.countActive : ''].filter(Boolean).join(' ')}>
          {count}
        </span>
        <Button
          variant="primary"
          size="sm"
          className={styles.counterButton}
          onClick={handleIncrement}
          disabled={disabled || count >= maxCount}
          aria-label={`Tambah ${labelMs}`}
        >
          +
        </Button>
      </div>
    </div>
  );
});

// ============================================================================
// Helper: resolve disabled state for a node (gender + blocking)
// Mirrors the SenaraiWarisTab logic centralised here for reuse by all sections.
// ============================================================================

function resolveNodeDisabled(
  heirType: HeirType,
  heirs: Record<HeirType, HeirInput>,
  deceased: DeceasedInfo,
): boolean {
  // Gender validity
  if (heirType === 'suami' && deceased.gender === 'lelaki') return true;
  if (heirType === 'isteri' && deceased.gender === 'perempuan') return true;

  // Hijab Hirman (blocked by closer heirs) — always-editable heirs bypass this
  if (!ALWAYS_EDITABLE_HEIRS.has(heirType)) {
    const { blockedBy } = HEIR_DEFINITIONS[heirType];
    if (blockedBy.some((blocker) => (heirs[blocker]?.count ?? 0) > 0)) {
      return true;
    }
  }

  return false;
}

// ============================================================================
// DeceasedNode -- the Si Mati centre node (non-editable)
// ============================================================================

interface DeceasedNodeProps {
  deceased: DeceasedInfo;
}

const DeceasedNode = ({ deceased }: DeceasedNodeProps) => {
  const isMale = deceased.gender === 'lelaki';
  return (
    <div
      className={[
        styles.heirNode,
        styles.deceasedNode,
        isMale ? styles.nodeMale : styles.nodeFemale,
      ].join(' ')}
    >
      <div className={styles.nodeAvatar}>
        {isMale ? <MalePersonIcon size={34} /> : <FemalePersonIcon size={34} />}
      </div>
      <div className={styles.nodeLabel}>SI MATI</div>
      <div className={styles.deceasedGenderBadge}>
        {isMale ? 'LELAKI' : 'PEREMPUAN'}
      </div>
    </div>
  );
};

// ============================================================================
// SiblingGroup -- "Adik Beradik Simati" panel
// ============================================================================

const SIBLING_PAIRS: [HeirType, HeirType][] = [
  ['saudara_seibu_sebapa_lelaki', 'saudara_seibu_sebapa_perempuan'],
  ['saudara_sebapa_lelaki', 'saudara_sebapa_perempuan'],
  ['saudara_seibu_lelaki', 'saudara_seibu_perempuan'],
];

interface SiblingGroupProps {
  heirs: Record<HeirType, HeirInput>;
  deceased: DeceasedInfo;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

const SiblingGroup = ({ heirs, deceased, onHeirChange }: SiblingGroupProps) => (
  <div className={styles.siblingGroup}>
    <div className={styles.siblingGroupTitle}>Adik Beradik Simati</div>
    <div className={styles.siblingGrid}>
      {SIBLING_PAIRS.map(([lelakiType, perempuanType]) => (
        <div key={`${lelakiType}-${perempuanType}`} className={styles.siblingRow}>
          <HeirNode
            heirType={lelakiType}
            input={heirs[lelakiType]}
            showWhenAbsent
            disabled={resolveNodeDisabled(lelakiType, heirs, deceased)}
            onHeirChange={onHeirChange}
          />
          <HeirNode
            heirType={perempuanType}
            input={heirs[perempuanType]}
            showWhenAbsent
            disabled={resolveNodeDisabled(perempuanType, heirs, deceased)}
            onHeirChange={onHeirChange}
          />
        </div>
      ))}
    </div>
  </div>
);

// ============================================================================
// NephewGroup
// ============================================================================

const NEPHEW_TYPES: HeirType[] = [
  'anak_saudara_seibu_sebapa_lelaki',
  'anak_saudara_sebapa_lelaki',
];

interface NephewGroupProps {
  heirs: Record<HeirType, HeirInput>;
  deceased: DeceasedInfo;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

const NephewGroup = ({ heirs, deceased, onHeirChange }: NephewGroupProps) => (
  <div className={styles.nephewGroup}>
    {NEPHEW_TYPES.map((t) => (
      <HeirNode
        key={t}
        heirType={t}
        input={heirs[t]}
        disabled={resolveNodeDisabled(t, heirs, deceased)}
        onHeirChange={onHeirChange}
      />
    ))}
  </div>
);

// ============================================================================
// ParentsRow
// ============================================================================

interface ParentsRowProps {
  heirs: Record<HeirType, HeirInput>;
  deceased: DeceasedInfo;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

const ParentsRow = ({ heirs, deceased, onHeirChange }: ParentsRowProps) => {
  const hasDatuk = (heirs['datuk']?.count ?? 0) > 0;
  const hasNenekBapa = (heirs['nenek_sebelah_bapa']?.count ?? 0) > 0;
  const hasNenekIbu = (heirs['nenek_sebelah_ibu']?.count ?? 0) > 0;
  const hasBapa = (heirs['bapa']?.count ?? 0) > 0;
  const hasIbu = (heirs['ibu']?.count ?? 0) > 0;

  return (
    <div className={styles.parentsRow}>
      <div className={styles.paternalColumn}>
        {hasDatuk && (
          <div className={styles.grandparentNode}>
            <HeirNode
              heirType="datuk"
              input={heirs['datuk']}
              disabled={resolveNodeDisabled('datuk', heirs, deceased)}
              onHeirChange={onHeirChange}
            />
            <div className={styles.connectorDown} />
          </div>
        )}
        {hasNenekBapa && (
          <div className={styles.grandparentNode}>
            <HeirNode
              heirType="nenek_sebelah_bapa"
              input={heirs['nenek_sebelah_bapa']}
              disabled={resolveNodeDisabled('nenek_sebelah_bapa', heirs, deceased)}
              onHeirChange={onHeirChange}
            />
            {!hasDatuk && <div className={styles.connectorDown} />}
          </div>
        )}
        {hasBapa && (
          <HeirNode
            heirType="bapa"
            input={heirs['bapa']}
            disabled={resolveNodeDisabled('bapa', heirs, deceased)}
            onHeirChange={onHeirChange}
          />
        )}
      </div>

      <div className={styles.maternalColumn}>
        {hasNenekIbu && (
          <div className={styles.grandparentNode}>
            <HeirNode
              heirType="nenek_sebelah_ibu"
              input={heirs['nenek_sebelah_ibu']}
              disabled={resolveNodeDisabled('nenek_sebelah_ibu', heirs, deceased)}
              onHeirChange={onHeirChange}
            />
            <div className={styles.connectorDown} />
          </div>
        )}
        {hasIbu && (
          <HeirNode
            heirType="ibu"
            input={heirs['ibu']}
            disabled={resolveNodeDisabled('ibu', heirs, deceased)}
            onHeirChange={onHeirChange}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SpouseGroup
// ============================================================================

interface SpouseGroupProps {
  deceased: DeceasedInfo;
  heirs: Record<HeirType, HeirInput>;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

const SpouseGroup = ({ deceased, heirs, onHeirChange }: SpouseGroupProps) => {
  const spouseType: HeirType = deceased.gender === 'lelaki' ? 'isteri' : 'suami';
  return (
    <div className={styles.spouseGroup}>
      <HeirNode
        heirType={spouseType}
        input={heirs[spouseType]}
        disabled={resolveNodeDisabled(spouseType, heirs, deceased)}
        onHeirChange={onHeirChange}
      />
    </div>
  );
};

// ============================================================================
// ChildrenRow
// ============================================================================

const DESCENDANT_GENERATIONS: { label: string; types: [HeirType, HeirType] }[] = [
  { label: 'Anak', types: ['anak_lelaki', 'anak_perempuan'] },
  { label: 'Cucu', types: ['cucu_lelaki', 'cucu_perempuan'] },
  { label: 'Cicit', types: ['cicit_lelaki', 'cicit_perempuan'] },
];

interface ChildrenRowProps {
  heirs: Record<HeirType, HeirInput>;
  deceased: DeceasedInfo;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

const ChildrenRow = ({ heirs, deceased, onHeirChange }: ChildrenRowProps) => (
  <div className={styles.descendantsSection}>
    {DESCENDANT_GENERATIONS.map(({ label, types }) => {
      const activeTypes = types.filter((t) => (heirs[t]?.count ?? 0) > 0);
      if (activeTypes.length === 0) return null;
      return (
        <div key={label} className={styles.generationRow}>
          <div className={styles.generationLabel}>{label}</div>
          <div className={styles.generationNodes}>
            {activeTypes.map((t) => (
              <HeirNode
                key={t}
                heirType={t}
                input={heirs[t]}
                disabled={resolveNodeDisabled(t, heirs, deceased)}
                onHeirChange={onHeirChange}
              />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

// ============================================================================
// ExtendedHeirsGroup
// ============================================================================

const EXTENDED_GROUPS: { label: string; types: HeirType[] }[] = [
  {
    label: 'Bapa Saudara',
    types: ['bapa_saudara_seibu_sebapa', 'bapa_saudara_sebapa'],
  },
  {
    label: 'Anak Bapa Saudara',
    types: ['anak_bapa_saudara_seibu_sebapa', 'anak_bapa_saudara_sebapa'],
  },
  {
    label: 'Datuk Saudara',
    types: [
      'datuk_saudara_seibu_sebapa',
      'datuk_saudara_sebapa',
      'anak_datuk_saudara_seibu_sebapa',
      'anak_datuk_saudara_sebapa',
    ],
  },
];

interface ExtendedHeirsGroupProps {
  heirs: Record<HeirType, HeirInput>;
  deceased: DeceasedInfo;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

const ExtendedHeirsGroup = ({ heirs, deceased, onHeirChange }: ExtendedHeirsGroupProps) => (
  <div className={styles.extendedSection}>
    <div className={styles.extendedTitle}>Waris Lain</div>
    <div className={styles.extendedGroups}>
      {EXTENDED_GROUPS.map((group) => {
        const activeTypes = group.types.filter((t) => (heirs[t]?.count ?? 0) > 0);
        if (activeTypes.length === 0) return null;
        return (
          <div key={group.label} className={styles.extendedGroup}>
            <div className={styles.extendedGroupLabel}>{group.label}</div>
            <div className={styles.extendedGroupNodes}>
              {activeTypes.map((t) => (
                <HeirNode
                  key={t}
                  heirType={t}
                  input={heirs[t]}
                  disabled={resolveNodeDisabled(t, heirs, deceased)}
                  onHeirChange={onHeirChange}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ============================================================================
// EmptyState
// ============================================================================

const EmptyState = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyStateIcon}>
      <MalePersonIcon size={28} />
    </div>
    <p className={styles.emptyStateText}>
      Tiada waris dipilih. Sila isi maklumat waris dalam tab{' '}
      <strong>Senarai Waris</strong> terlebih dahulu.
    </p>
  </div>
);

// ============================================================================
// CartaWarisTab -- main export
// ============================================================================

export const CartaWarisTab = ({ deceased, heirs, onHeirChange }: CartaWarisTabProps) => {
  const hasAnyHeir = useMemo(
    () => Object.values(heirs).some((h) => h.count > 0),
    [heirs],
  );

  // Derived visibility flags — memoised so section components only re-render
  // when heirs actually changes, not on every parent render.
  const {
    hasParents,
    hasSiblings,
    hasNephews,
    hasSpouse,
    hasDescendants,
    hasExtended,
  } = useMemo(() => {
    const hasParents =
      (heirs['bapa']?.count ?? 0) > 0 ||
      (heirs['ibu']?.count ?? 0) > 0 ||
      (heirs['datuk']?.count ?? 0) > 0 ||
      (heirs['nenek_sebelah_bapa']?.count ?? 0) > 0 ||
      (heirs['nenek_sebelah_ibu']?.count ?? 0) > 0;

    const hasSiblings = SIBLING_PAIRS.some(
      ([l, p]) => (heirs[l]?.count ?? 0) > 0 || (heirs[p]?.count ?? 0) > 0,
    );

    const hasNephews = NEPHEW_TYPES.some((t) => (heirs[t]?.count ?? 0) > 0);

    const hasSpouse =
      (heirs['suami']?.count ?? 0) > 0 || (heirs['isteri']?.count ?? 0) > 0;

    const hasDescendants = DESCENDANT_GENERATIONS.some(({ types }) =>
      types.some((t) => (heirs[t]?.count ?? 0) > 0),
    );

    const hasExtended = EXTENDED_GROUPS.some((g) =>
      g.types.some((t) => (heirs[t]?.count ?? 0) > 0),
    );

    return { hasParents, hasSiblings, hasNephews, hasSpouse, hasDescendants, hasExtended };
  }, [heirs]);

  if (!hasAnyHeir) {
    return (
      <Card className={styles.chartCard} noPadding>
        <EmptyState />
      </Card>
    );
  }

  return (
    <Card className={styles.chartCard} noPadding>
      <div className={styles.chartScrollArea}>
        <div className={styles.chartCanvas}>

          {/* Upper section: Siblings (left) and Parents (right) */}
          {(hasParents || hasSiblings || hasNephews) && (
            <div className={styles.upperSection}>
              {(hasSiblings || hasNephews) && (
                <div className={styles.leftSection}>
                  {hasSiblings && (
                    <SiblingGroup heirs={heirs} deceased={deceased} onHeirChange={onHeirChange} />
                  )}
                  {hasNephews && (
                    <NephewGroup heirs={heirs} deceased={deceased} onHeirChange={onHeirChange} />
                  )}
                </div>
              )}
              {hasParents && (
                <div className={styles.rightSection}>
                  <ParentsRow heirs={heirs} deceased={deceased} onHeirChange={onHeirChange} />
                </div>
              )}
            </div>
          )}

          {(hasParents || hasSiblings) && (
            <div className={styles.upperConnectorRow}>
              {(hasSiblings || hasNephews) && <div className={styles.connectorSiblingToSimati} />}
              {hasParents && <div className={styles.connectorParentsToSimati} />}
            </div>
          )}

          {/* Middle: Si Mati + Spouse */}
          <div className={styles.middleRow}>
            <div className={styles.simatiWrapper}>
              <DeceasedNode deceased={deceased} />
            </div>
            {hasSpouse && (
              <>
                <div className={styles.spouseConnector} />
                <SpouseGroup deceased={deceased} heirs={heirs} onHeirChange={onHeirChange} />
              </>
            )}
          </div>

          {hasDescendants && <div className={styles.descendantConnector} />}

          {/* Bottom: Descendants */}
          {hasDescendants && (
            <ChildrenRow heirs={heirs} deceased={deceased} onHeirChange={onHeirChange} />
          )}

          {/* Extended heirs */}
          {hasExtended && (
            <ExtendedHeirsGroup heirs={heirs} deceased={deceased} onHeirChange={onHeirChange} />
          )}

        </div>
      </div>
    </Card>
  );
};
